import { html, LitElement, unsafeCSS } from 'lit';
import { property } from 'lit/decorators/property.js';
import { customElement } from 'lit/decorators/custom-element.js';
import {
    Pagination,
    PaginationInterface
} from '../../library/class/pagination.ts';
import {
    Filters,
    FiltersInterface
} from '../../library/class/filters.class.ts';
import cardListCSS from './bg-card-list.scss?inline';
import { SlCard } from '@shoelace-style/shoelace';
import { getBlockAlignment } from '../../library/design-helpers.ts';
import { classMap } from 'lit/directives/class-map.js';

// Important! "shoelaceElementFactory" is used to announce the use of Shoelace components
// @ts-ignore
const shoelaceElementFactory = (tagName: string) => {
    // Define a list of all Shoelace components being used
    customElements.define(tagName, SlCard);
};

export interface BgCardListInterface {
    pagination?: PaginationInterface;
    filters?: FiltersInterface;
    blockAlignment?: string;
    colorScheme?: string; /* used by storybook only */
    cardStyle?: string; /* used by storybook only */
}

/**
 * BgCardList
 * Pagination and filtering are included, however they are not responsible for providing the necessary page content of cards,
 * instead the pagination and filtering will change the current URL to the selected page
 * It is the responsibility of the back-office or parent component to take care of presenting the required page content.
 * @slot - This element has only a default slot, this should be used to provide card contents.
 */
@customElement('bg-card-list')
export class BgCardList extends LitElement {
    @property()
    docsHint = '';

    @property({ type: Object })
    pagination?: PaginationInterface = undefined;

    @property({ type: Object })
    filters?: FiltersInterface = undefined;

    @property({ type: String, attribute: 'block-alignment' })
    blockAlignment?: string;

    constructor(args: BgCardListInterface) {
        super();
        if (!args) {
            return;
        };
        this.pagination = args.pagination;
        this.filters = args.filters;
    }

    private _pagination?: Pagination;
    private _filters?: Filters;

    render = () => {
        const filtersURL = getFiltersFromUrl();
        this._pagination = new Pagination(this.pagination, filtersURL);
        this._filters = new Filters(this.filters);
        const filters = this._filters.renderFilters();
        const pagination = this._pagination?.renderPagination() || '';
        const classMapping = {
            'bg-card-list': true,
            ...getBlockAlignment(this.blockAlignment || ''),
        };

        return html`
            ${filters}
            <div class="${classMap(classMapping)}" part="cards">
                <slot></slot>
            </div>
            ${pagination}
            <style>
                .bg-card-list {
                    color: var(--bg-color-foreground);
                    ${this.style}
                }
            </style>
        `;
    };

    static styles = unsafeCSS(cardListCSS);

    /* Start manage event when selecting a value from the dropdown */
    firstUpdated = () => {
        const filterDropdown = this.shadowRoot?.querySelector('sl-dropdown');
        if (!filterDropdown) {
            return;
        }

        filterDropdown.addEventListener('sl-select', (event) => {
            const selectedItem = event.detail.item;
            window.top?.location.assign(selectedItem.value);
        });
    };
    /* End manage event when selecting a value from the dropdown */
}

declare global {
    interface HTMLElementTagNameMap {
        'bg-card-list': BgCardList;
    }
}

function getFiltersFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const filter = urlParams.get('filter');
    return filter ? `&filter=${filter}` : '';

}
